import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Report, useGetScalingSize } from '@ui/Report';

import {
  COVERAGE_DONUT_DIAMETER,
  COVERAGE_DONUT_LABEL_GAP,
} from './report-product-groups.constants';
import {
  PRODUCT_GROUPS_PROPOSAL_SUMMARY,
  PRODUCT_GROUPS_TEXT_COLOR,
} from './report-product-groups.typography';

const { Text } = Report;

const DONUT_STROKE_RATIO = 0.08;
const CIRCLE_RADIUS = 50;
const VIEWBOX_SIZE = 100;
const STROKE_WIDTH = VIEWBOX_SIZE * DONUT_STROKE_RATIO;
const NORMALIZED_RADIUS = CIRCLE_RADIUS - STROKE_WIDTH / 2;
const CIRCUMFERENCE = 2 * Math.PI * NORMALIZED_RADIUS;

const PROGRESS_CAP_RADIUS = STROKE_WIDTH / 12;

const TRACK_COLOR = 'rgba(40, 42, 49, 0.15)';
const FILL_COLOR = PRODUCT_GROUPS_TEXT_COLOR;

const pointOnDonut = (angleDeg: number) => {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CIRCLE_RADIUS + NORMALIZED_RADIUS * Math.cos(rad),
    y: CIRCLE_RADIUS + NORMALIZED_RADIUS * Math.sin(rad),
  };
};

type CoverageDonutProps = {
  /** Coverage percentage (0–100). Values are clamped. */
  percent: number;
  /** Optional label shown to the left of the donut. */
  label?: string;
};

/**
 * Tiny percentage donut shown next to section headers (e.g. "Supplier data (EPD)").
 * Renders a centered percentage label inside a thin ring.
 */
export const CoverageDonut: React.FC<CoverageDonutProps> = ({
  percent,
  label,
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({ getScalingSize });

  const clamped = Math.max(0, Math.min(100, percent));
  const dashOffset = CIRCUMFERENCE * (1 - clamped / 100);
  const caption = PRODUCT_GROUPS_PROPOSAL_SUMMARY.caption;
  const progressStart = pointOnDonut(-90);
  const progressEnd = pointOnDonut(-90 + (clamped / 100) * 360);
  const showProgressCaps = clamped > 0 && clamped < 100;

  return (
    <Box className={classes.root}>
      {label && (
        <Text
          as="span"
          fontSize={caption.fontSize}
          fontWeight={500}
          sx={{
            color: PRODUCT_GROUPS_TEXT_COLOR,
            lineHeight: caption.lineHeight,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Text>
      )}
      <Box className={classes.donutWrapper}>
        <svg
          viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
          xmlns="http://www.w3.org/2000/svg"
          className={classes.svg}
        >
          <circle
            cx={CIRCLE_RADIUS}
            cy={CIRCLE_RADIUS}
            r={NORMALIZED_RADIUS}
            fill="none"
            stroke={TRACK_COLOR}
            strokeWidth={STROKE_WIDTH}
          />
          {clamped > 0 && (
            <circle
              cx={CIRCLE_RADIUS}
              cy={CIRCLE_RADIUS}
              r={NORMALIZED_RADIUS}
              fill="none"
              stroke={FILL_COLOR}
              strokeWidth={STROKE_WIDTH}
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="butt"
              transform={`rotate(-90 ${CIRCLE_RADIUS} ${CIRCLE_RADIUS})`}
            />
          )}
          {showProgressCaps && (
            <>
              <circle
                cx={progressStart.x}
                cy={progressStart.y}
                r={PROGRESS_CAP_RADIUS}
                fill={FILL_COLOR}
              />
              <circle
                cx={progressEnd.x}
                cy={progressEnd.y}
                r={PROGRESS_CAP_RADIUS}
                fill={FILL_COLOR}
              />
            </>
          )}
        </svg>
        <Text
          as="span"
          fontSize={caption.fontSize}
          fontWeight={500}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '53%',
            transform: 'translate(-50%, -50%)',
            color: PRODUCT_GROUPS_TEXT_COLOR,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {Math.round(clamped)}%
        </Text>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()((_theme, { getScalingSize }) => ({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: getScalingSize(COVERAGE_DONUT_LABEL_GAP),
  },
  donutWrapper: {
    position: 'relative',
    flexShrink: 0,
    width: getScalingSize(COVERAGE_DONUT_DIAMETER),
    height: getScalingSize(COVERAGE_DONUT_DIAMETER),
  },
  svg: {
    display: 'block',
    width: '100%',
    height: '100%',
  },
}));
